import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const DAY_FULL_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

const WEEKDAYS = [1, 2, 3, 4, 5]; // Mon-Fri
const WEEKENDS = [0, 6]; // Sun, Sat
const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6];

interface DayCheckboxGroupProps {
  value: number[];
  onChange: (days: number[]) => void;
  disabled?: boolean;
  showPresets?: boolean;
  showSummary?: boolean;
}

export function DayCheckboxGroup({
  value,
  onChange,
  disabled = false,
  showPresets = true,
  showSummary = true,
}: DayCheckboxGroupProps) {
  const { t } = useTranslation('staff');

  const getDayLabel = (dayIndex: number) => t(`availability.days.${DAY_KEYS[dayIndex]}`);
  const getDayFullName = (dayIndex: number) => t(`availability.days.${DAY_FULL_KEYS[dayIndex]}`);

  const toggleDay = (day: number) => {
    if (disabled) return;
    if (value.includes(day)) {
      onChange(value.filter(d => d !== day));
    } else {
      onChange([...value, day].sort((a, b) => a - b));
    }
  };

  const applyPreset = (preset: number[]) => {
    if (disabled) return;
    onChange(preset);
  };

  const getSummary = () => {
    if (value.length === 0) return t('availability.dayCheckbox.noDaysSelected');
    if (value.length === 7) return t('availability.dayCheckbox.everyDay');

    // Check for weekdays
    const isWeekdays = WEEKDAYS.every(d => value.includes(d)) &&
                       !value.includes(0) && !value.includes(6);
    if (isWeekdays) return t('availability.dayCheckbox.weekdaysSummary');

    // Check for weekends
    const isWeekends = WEEKENDS.every(d => value.includes(d)) && value.length === 2;
    if (isWeekends) return t('availability.dayCheckbox.weekendsSummary');

    // List individual days
    const dayNames = value.map(d => getDayFullName(d));
    if (dayNames.length <= 3) {
      return dayNames.join(', ');
    }
    return `${dayNames.slice(0, 2).join(', ')} ${t('availability.dayCheckbox.more', { count: dayNames.length - 2 })}`;
  };

  return (
    <div className="space-y-4">
      {/* Preset buttons */}
      {showPresets && (
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => applyPreset(WEEKDAYS)}
          disabled={disabled}
          className={cn(
            'text-sm',
            WEEKDAYS.every(d => value.includes(d)) &&
            !value.includes(0) && !value.includes(6) &&
            'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
          )}
        >
          {t('availability.dayCheckbox.weekdays')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => applyPreset(WEEKENDS)}
          disabled={disabled}
          className={cn(
            'text-sm',
            WEEKENDS.every(d => value.includes(d)) && value.length === 2 &&
            'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
          )}
        >
          {t('availability.dayCheckbox.weekends')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => applyPreset(EVERY_DAY)}
          disabled={disabled}
          className={cn(
            'text-sm',
            value.length === 7 && 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
          )}
        >
          {t('availability.dayCheckbox.everyDay')}
        </Button>
        {value.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => onChange([])}
            disabled={disabled}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {t('availability.dayCheckbox.clear')}
          </Button>
        )}
      </div>
      )}

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-2 md:gap-3">
        {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
          const isSelected = value.includes(dayIndex);
          return (
            <button
              key={dayIndex}
              type="button"
              onClick={() => toggleDay(dayIndex)}
              disabled={disabled}
              className={cn(
                'flex flex-col items-center justify-center px-1',
                'min-h-[60px] md:min-h-[64px] rounded-lg border-2 transition-all',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'active:scale-95',
                isSelected
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span className="text-sm font-medium">{getDayLabel(dayIndex)}</span>
              {isSelected && (
                <svg
                  className="w-4 h-4 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      {/* Summary */}
      {showSummary && (
        <p className="text-sm text-gray-600">
          <span className="font-medium">{t('availability.dayCheckbox.selected')}:</span> {getSummary()}
        </p>
      )}
    </div>
  );
}
